import Konva from 'konva';
import type { ComponentDef } from '../../core/library/component-def.js';
import { renderPinLabel } from './pin-label.js';
import { addUnifiedValueLabel } from './value-label.js';

/**
 * Renderer del cuerpo de un diodo axial (cristal negro con banda blanca
 * en el cátodo). Similar a resistencia cilíndrica pero con banda estrecha
 * de tolerancia que marca el pin K (a la derecha en nuestra def).
 * Etiqueta con el valor centrada, con contra-transformación.
 */
export function renderDiodeBody(
  def: ComponentDef,
  labelText: string,
  rotationDeg: number = 0,
  flipped: boolean = false,
): Konva.Group {
  const g = new Konva.Group();

  const length = def.body.length_mm ?? 5.08;
  const diameter = def.body.diameter_mm ?? 2.54;
  const cornerRadius = def.body.corner_radius_mm ?? 0.6;

  const bodyColor = def.appearance.body_color;
  const light = mix(bodyColor, '#ffffff', 0.35);
  const dark = mix(bodyColor, '#000000', 0.3);

  const capsule = new Konva.Rect({
    x: -length / 2,
    y: -diameter / 2,
    width: length,
    height: diameter,
    cornerRadius,
    fillLinearGradientStartPoint: { x: 0, y: -diameter / 2 },
    fillLinearGradientEndPoint: { x: 0, y: diameter / 2 },
    fillLinearGradientColorStops: [
      0, dark,
      0.4, bodyColor,
      0.55, light,
      1, dark,
    ],
    stroke: dark,
    strokeWidth: 0.08,
    listening: true,
  });
  capsule.setAttr('nodeKind', 'body');
  g.add(capsule);

  // Banda cátodo (blanca) cerca del pin K (extremo derecho).
  const bandWidth = length * 0.15;
  const bandX = length / 2 - length * 0.22;
  g.add(
    new Konva.Rect({
      x: bandX - bandWidth / 2,
      y: -diameter / 2,
      width: bandWidth,
      height: diameter,
      fillLinearGradientStartPoint: { x: 0, y: -diameter / 2 },
      fillLinearGradientEndPoint: { x: 0, y: diameter / 2 },
      fillLinearGradientColorStops: [
        0, '#dddddd',
        0.5, '#ffffff',
        1, '#dddddd',
      ],
      listening: false,
    }),
  );

  // Etiqueta con contra-transformación (igual criterio que resistencia).
  const target = Math.abs(rotationDeg % 180) === 0 ? 0 : 90;
  const ps = flipped ? -1 : 1;
  const labelGroup = new Konva.Group({
    rotation: ps * (target - rotationDeg),
    scaleY: ps,
    listening: false,
  });
  const fontSize = def.appearance.label?.font_size_mm ?? diameter * 0.45;
  addUnifiedValueLabel(labelGroup, labelText, fontSize, length * 0.7);
  g.add(labelGroup);

  // Etiquetas A / K junto a cada pin, fuera del cuerpo cerca de éste.
  const labelFont = Math.min(diameter * 0.55, 1.2);
  for (const pin of def.pins) {
    g.add(renderPinLabel(pin, pin.id, 1.4, rotationDeg, flipped, labelFont));
  }

  return g;
}

function mix(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  return `#${toHex(ca.r + (cb.r - ca.r) * t)}${toHex(ca.g + (cb.g - ca.g) * t)}${toHex(ca.b + (cb.b - ca.b) * t)}`;
}
function parseHex(hex: string): { r: number; g: number; b: number } {
  let s = hex.replace('#', '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  return {
    r: parseInt(s.slice(0, 2), 16),
    g: parseInt(s.slice(2, 4), 16),
    b: parseInt(s.slice(4, 6), 16),
  };
}
function toHex(n: number): string {
  const v = Math.round(Math.max(0, Math.min(255, n)));
  return v.toString(16).padStart(2, '0');
}
